import merge from "lodash/merge"
import uniqueId from "lodash/uniqueId"
import React, {
  type PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react"
import useSleep from "../util/hooks/useSleep"
import ToastContainer from "./ToastContainer"
import type {
  AddToastFn,
  Toast,
  ToastContextState,
  ToastOptions,
} from "./types"

const ToastContext = React.createContext<ToastContextState | null>(null)

function toastOptionDefaults(options: ToastOptions): Required<ToastOptions> {
  return {
    life: 4200,
    position: "bottom-right",
    width: 320,
    height: 72,

    contentPadding: "1rem 1.5rem 1rem 1rem",
    contentAlignHorizontal: "left",
    contentAlignVertical: "center",

    color: "text.light",
    background: "gray1",

    borderRadius: "4px",
    boxShadow: "0 2px 6px 1px rgba(0, 0, 0, 0.2)",

    iconWidth: 56,
    progressBarColor: "#d17117",
    progressBarHeight: "4px",

    showCloseIcon: true,
    showProgressBar: true,

    ...options,

    icons:
      options.icons === null
        ? null
        : {
            success:
              options.icons?.success === null
                ? null
                : { icon: "check", color: "success", ...options.icons?.all },
            error:
              options.icons?.error === null
                ? null
                : { icon: "times", color: "error", ...options.icons?.all },
            warning:
              options.icons?.warning === null
                ? null
                : {
                    icon: "exclamation",
                    type: "solid",
                    color: "warning",
                    ...options.icons?.all,
                  },
            info:
              options.icons?.info === null
                ? null
                : {
                    icon: "info",
                    type: "solid",
                    color: "info",
                    ...options.icons?.all,
                  },
            ...(options.icons ?? {}),
          },
  }
}

export function useToast(defaultOptions?: ToastOptions) {
  const ctx = useContext(ToastContext)
  if (ctx === null)
    throw new Error("useToast can only be used within a ToastProvider.")

  const callback: AddToastFn = (type, content, options) =>
    ctx.addToast(type, content, merge(defaultOptions, options))
  return callback
}
function useToastState(defaultOptions?: ToastOptions): ToastContextState {
  const sleep = useSleep()

  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast: AddToastFn = useCallback(
    async (type, content, options) => {
      const {
        life,
        position,
        width,
        height,
        contentPadding,
        contentAlignHorizontal,
        contentAlignVertical,
        color,
        background,
        borderRadius,
        boxShadow,
        icons,
        iconWidth,
        showCloseIcon,
        showProgressBar,
        progressBarColor,
        progressBarHeight,
      } = toastOptionDefaults(merge(defaultOptions ?? {}, options ?? {}))

      const id = uniqueId("toast")
      const newToast: Toast = {
        id,
        type,
        content: typeof content === "string" ? <>{content}</> : content,
        life,
        position,
        width,
        height,
        contentPadding,
        contentAlignHorizontal,
        contentAlignVertical,
        color,
        background,
        borderRadius,
        boxShadow,
        icons,
        iconWidth,
        showCloseIcon,
        showProgressBar,
        progressBarColor,
        progressBarHeight,
        persistent: life === 0,
        close: () => setToasts((d) => d.filter((t) => t.id !== id)),
      }
      setToasts((t) => [...t, newToast])

      if (!newToast.persistent) {
        await sleep(newToast.life)
        newToast.close()
      }
    },
    [defaultOptions, sleep],
  )

  return {
    toasts,
    addToast,
  }
}

export interface ToastProviderProps extends PropsWithChildren<object> {
  options?: ToastOptions
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  options = {},
}) => {
  const state = useToastState(options)

  return (
    <ToastContext.Provider value={state}>
      <ToastContainer toasts={state.toasts} />
      {children}
    </ToastContext.Provider>
  )
}
